import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, Eye, EyeOff, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { setAuth } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GRADES = ["Grade 8", "Grade 9", "Grade 10", "Grade 11", "Grade 12", "Undergraduate", "Postgraduate", "Career Professional"];
const FUTURE_PLANS = [
  { value: "International", label: "International - Study Abroad" },
  { value: "Domestic", label: "Domestic - Study in India" },
  { value: "Job Seeker", label: "Job Seeker" },
];

function fmtErr(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const inputCls = "w-full rounded-2xl border border-ink/15 bg-cream px-5 py-3.5 text-sm outline-none focus:border-iqblue focus:ring-4 focus:ring-iqblue/15 transition-all placeholder:text-ink/35";

const HEADINGS = {
  signup: ["Create your account", "Sign up to take the test and save your results"],
  login: ["Welcome back", "Log in to continue your journey"],
  forgot: ["Reset your password", "We'll email you a secure reset link"],
};

export default function AuthModal({ open, onClose, onSuccess }) {
  const [mode, setMode] = useState("signup");
  const [form, setForm] = useState({ name: "", email: "", mobile: "", grade: GRADES[2], school: "", study_location: "", password: "", whatsapp_opt_in: true, institution_code: "" });
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [otp, setOtp] = useState(null); // { signupId, channels, email, mobile }
  const [codes, setCodes] = useState({ email: "", mobile: "" });
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const iv = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(iv);
  }, [cooldown]);

  const resendOtp = async (channel) => {
    try {
      const res = await fetch(`${API}/auth/register/resend`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signup_id: otp.signupId, channel }),
      });
      const data = await res.json();
      if (!res.ok) toast.error(fmtErr(data.detail));
      else { toast.success("New code sent"); setCooldown(60); }
    } catch { toast.error("Network error. Please try again."); }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await fetch(`${API}/auth/register/verify`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signup_id: otp.signupId, email_otp: codes.email, mobile_otp: codes.mobile }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(fmtErr(data.detail));
        setBusy(false);
        return;
      }
      setAuth(data.token, data.user);
      toast.success(`Welcome${data.user?.name ? `, ${data.user.name.split(" ")[0]}` : ""}!`);
      setBusy(false);
      setOtp(null);
      onSuccess?.(data.user);
      onClose();
    } catch {
      toast.error("Network error. Please try again.");
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "forgot") {
        const res = await fetch(`${API}/auth/forgot-password`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: form.email }),
        });
        const data = await res.json();
        if (!res.ok) toast.error(fmtErr(data.detail));
        else setResetSent(true);
        setBusy(false);
        return;
      }
      const res = await fetch(`${API}/auth/${mode === "login" ? "login" : "register"}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(
          mode === "login"
            ? { email: form.email, password: form.password }
            : { name: form.name, email: form.email, password: form.password, mobile: form.mobile, grade: form.grade, school: form.school, study_location: form.study_location, whatsapp_opt_in: form.whatsapp_opt_in, institution_code: form.institution_code.trim() || null }
        ),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(fmtErr(data.detail));
        setBusy(false);
        return;
      }
      if (mode === "signup" && data.otpRequired) {
        setOtp({ signupId: data.signupId, channels: data.channels, email: data.email, mobile: data.mobile });
        setCodes({ email: "", mobile: "" });
        setCooldown(60);
        toast.success("Verification code sent to your email");
        setBusy(false);
        return;
      }
      setAuth(data.token, data.user);
      if (mode === "signup" && form.whatsapp_opt_in) {
        toast.success(data.whatsappSent ? "Confirmation sent on WhatsApp too" : "Welcome email sent — WhatsApp confirmation will follow once enabled");
      }
      toast.success(`Welcome${data.user?.name ? `, ${data.user.name.split(" ")[0]}` : ""}!`);
      setBusy(false);
      onSuccess?.(data.user);
      onClose();
    } catch {
      toast.error("Network error. Please try again.");
      setBusy(false);
    }
  };

  const [h1, h2] = HEADINGS[mode];

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[85] flex items-center justify-center p-4">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-ink/70 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            data-testid="auth-modal"
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.97 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-md bg-paper rounded-[1.8rem] shadow-2xl p-8 max-h-[88vh] overflow-y-auto"
          >
            <button onClick={onClose} data-testid="auth-close-button" className="absolute top-4 right-4 h-9 w-9 rounded-full bg-ink/5 hover:bg-ink/10 flex items-center justify-center transition-colors" aria-label="Close">
              <X className="h-4 w-4" />
            </button>
            <AnimatePresence mode="wait">
              <motion.div key={otp ? "otp" : mode} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.25 }}>
                <h3 className="font-display font-bold text-2xl tracking-tight">{otp ? "Verify your details" : h1}</h3>
                <p className="mt-2 text-sm text-ink/60">{otp ? `We sent a 6-digit code to ${otp.email}${otp.channels.includes("mobile") ? ` and an SMS to ${otp.mobile}` : ""}. Codes expire in 10 minutes.` : h2}</p>

                {otp ? (
                  <form onSubmit={verifyOtp} className="mt-6 space-y-3" data-testid="otp-step">
                    <label className="block text-xs font-bold tracking-wide text-ink/60 uppercase">Email code</label>
                    <input data-testid="otp-email-input" inputMode="numeric" maxLength={6} value={codes.email} onChange={(e) => setCodes({ ...codes, email: e.target.value.replace(/\D/g, "") })} placeholder="6-digit code from email" required className={`${inputCls} text-center text-xl tracking-[0.5em] font-bold`} />
                    {otp.channels.includes("mobile") && (
                      <>
                        <label className="block text-xs font-bold tracking-wide text-ink/60 uppercase">Mobile (SMS) code</label>
                        <input data-testid="otp-mobile-input" inputMode="numeric" maxLength={6} value={codes.mobile} onChange={(e) => setCodes({ ...codes, mobile: e.target.value.replace(/\D/g, "") })} placeholder="6-digit code from SMS" required className={`${inputCls} text-center text-xl tracking-[0.5em] font-bold`} />
                      </>
                    )}
                    <button type="submit" disabled={busy} data-testid="otp-verify-button" className="w-full flex items-center justify-center gap-2 bg-ink text-paper font-semibold py-4 rounded-full hover:bg-iqblue disabled:opacity-60 transition-colors duration-300">
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & create account"}
                    </button>
                    <div className="flex items-center justify-between text-xs font-semibold pt-1">
                      <button type="button" onClick={() => setOtp(null)} data-testid="otp-back-button" className="text-ink/50 hover:text-ink transition-colors">← Edit details</button>
                      <button type="button" disabled={cooldown > 0} onClick={() => resendOtp("email")} data-testid="otp-resend-email" className="text-iqblue hover:underline disabled:text-ink/30 disabled:no-underline">
                        {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend email code"}
                      </button>
                      {otp.channels.includes("mobile") && (
                        <button type="button" disabled={cooldown > 0} onClick={() => resendOtp("mobile")} data-testid="otp-resend-mobile" className="text-iqblue hover:underline disabled:text-ink/30 disabled:no-underline">
                          Resend SMS
                        </button>
                      )}
                    </div>
                  </form>
                ) : (
                <>
                {mode !== "forgot" && (
                  <div className="mt-6 grid grid-cols-2 rounded-full border border-ink/15 p-1">
                    {["signup", "login"].map((m) => (
                      <button key={m} type="button" data-testid={`auth-tab-${m}`} onClick={() => setMode(m)} className={`py-2 rounded-full text-sm font-semibold transition-colors duration-300 ${mode === m ? "bg-ink text-paper" : "text-ink/60 hover:text-ink"}`}>
                        {m === "signup" ? "Create account" : "Sign in"}
                      </button>
                    ))}
                  </div>
                )}

                {resetSent ? (
                  <div className="mt-7 flex items-start gap-3 rounded-2xl bg-iqgreen/10 border border-iqgreen/25 px-5 py-4" data-testid="auth-reset-sent">
                    <MailCheck className="h-5 w-5 text-iqgreen shrink-0 mt-0.5" />
                    <p className="text-sm text-ink/70 leading-relaxed">If an account exists for <b>{form.email}</b>, a reset link is on its way. It expires in 60 minutes.</p>
                  </div>
                ) : (
                  <form onSubmit={submit} className="mt-5 space-y-3">
                    {mode === "signup" && (
                      <>
                        <input data-testid="auth-name-input" value={form.name} onChange={set("name")} placeholder="Full name" required className={inputCls} />
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <input data-testid="auth-mobile-input" type="tel" value={form.mobile} onChange={set("mobile")} placeholder="Mobile number *" required className={inputCls} />
                          <select data-testid="auth-grade-select" value={form.grade} onChange={set("grade")} className={`${inputCls} text-ink/80`}>
                            {GRADES.map((g) => <option key={g}>{g}</option>)}
                          </select>
                        </div>
                        <input data-testid="auth-email-input" type="email" value={form.email} onChange={set("email")} placeholder="Email *" required className={inputCls} />
                        <input data-testid="auth-school-input" value={form.school} onChange={set("school")} placeholder="School / College *" required className={inputCls} />
                        <input data-testid="auth-institution-code-input" value={form.institution_code} onChange={(e) => setForm({ ...form, institution_code: e.target.value.toUpperCase() })} placeholder={form.grade === "Career Professional" ? "Company code (optional — from your employer)" : "Institution code (optional — from your school/college)"} className={inputCls} />
                        <div>
                          <select id="auth-future-plan" data-testid="auth-location-select" value={form.study_location} onChange={set("study_location")} required className={`${inputCls} ${form.study_location ? "text-ink/80" : "text-ink/35"}`}>
                            <option value="" disabled>Future Plans</option>
                            {FUTURE_PLANS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                          </select>
                        </div>
                        <label data-testid="auth-whatsapp-optin" className="flex items-center gap-3 rounded-2xl border border-ink/15 bg-white px-5 py-3.5 cursor-pointer select-none">
                          <input type="checkbox" checked={form.whatsapp_opt_in} onChange={(e) => setForm({ ...form, whatsapp_opt_in: e.target.checked })} data-testid="auth-whatsapp-checkbox" className="h-4 w-4 accent-[#25D366]" />
                          <span className="text-xs text-ink/70 font-medium">Send my confirmation & report alerts on <b>WhatsApp</b> too</span>
                        </label>
                      </>
                    )}
                    {mode !== "signup" && (
                      <input data-testid="auth-email-input" type="email" value={form.email} onChange={set("email")} placeholder="Email *" required className={inputCls} />
                    )}
                    {mode !== "forgot" && (
                      <div className="relative">
                        <input data-testid="auth-password-input" type={showPw ? "text" : "password"} value={form.password} onChange={set("password")} placeholder={mode === "signup" ? "Password (min 6 characters)" : "Password"} required minLength={6} className={`${inputCls} pr-12`} />
                        <button type="button" onClick={() => setShowPw(!showPw)} data-testid="auth-password-toggle" className="absolute right-4 top-1/2 -translate-y-1/2 text-ink/40 hover:text-ink transition-colors" aria-label="Toggle password visibility">
                          {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    )}
                    <button type="submit" disabled={busy} data-testid="auth-submit-button" className="w-full flex items-center justify-center gap-2 bg-ink text-paper font-semibold py-4 rounded-full hover:bg-iqblue disabled:opacity-60 transition-colors duration-300">
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === "signup" ? "Create account & continue" : mode === "login" ? "Log in" : "Email me a reset link"}
                    </button>
                  </form>
                )}

                <div className="mt-4 text-center">
                  {mode === "login" && (
                    <button type="button" onClick={() => { setMode("forgot"); setResetSent(false); }} data-testid="forgot-password-link" className="text-xs font-semibold text-iqblue hover:underline">
                      Forgot password?
                    </button>
                  )}
                  {mode === "forgot" && (
                    <button type="button" onClick={() => { setMode("login"); setResetSent(false); }} data-testid="back-to-login-link" className="text-xs font-semibold text-iqblue hover:underline">
                      Back to log in
                    </button>
                  )}
                </div>
                </>
                )}
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
